import React, {
   useState,
   forwardRef,
   useImperativeHandle,
   useEffect,
   useRef,
   useCallback,
} from "react";
import infoIcon from "../../assets/admin/icons/info.svg";
import CustomCheckbox from "./CustomCheckbox";
import "./InputWithChecks.css";

const InputWithChecks = forwardRef(
   (
      {
         label,
         inputType,
         validate,
         children,
         info,
         value = "",
         options = [],
         placeholder = "Selecteer...",
         className = "",
         ...props
      },
      ref
   ) => {
      const [isFocused, setIsFocused] = useState(false);
      const [inputValue, setInputValue] = useState(value);
      const [errorMessage, setErrorMessage] = useState("");
      const [showTooltip, setShowTooltip] = useState(false);

      const [dropdownOpen, setDropdownOpen] = useState(false);
      const [searchTerm, setSearchTerm] = useState("");
      const [selectedOptions, setSelectedOptions] = useState([]);

      const dropDownValuesHiddenRef = useRef();
      const componentRef = useRef(null);

      useEffect(() => {
         if (inputType !== "multipleSearchDropdown") {
            setInputValue(value);
         }
      }, [value, inputType]);

      useEffect(() => {
         if (inputType === "multipleSearchDropdown" && value) {
            const alreadySelected = [];

            for (const includedId of value) {
               let product = options.find((opt) => opt.id === includedId);
               if (product) {
               alreadySelected.push({
                     id: includedId,
                  label: product.label,
                  });
               }
            }
            const labels = alreadySelected.map((o) => o.label).join(", ");
            if (dropDownValuesHiddenRef.current) {
            dropDownValuesHiddenRef.current.value = value.join(",");
            }

            setSelectedOptions(alreadySelected);
            setInputValue(labels);
         }
      }, [value, inputType, options]);

      const handleBlur = () => {
         if (inputType !== "multipleSearchDropdown") {
            setIsFocused(false);
            if (validate) {
               const validationResult = validate(inputValue);
               setErrorMessage(validationResult || "");
            }
         }
      };

      const handleFocus = () => {
         setIsFocused(true);
      };

      const handleChange = (e) => {
         setInputValue(e.target.value);
         if (isFocused && validate) {
            setErrorMessage(validate(e.target.value));
         }
      };

      useImperativeHandle(ref, () => ({
         validateInput: () => {
            if (inputType !== "multipleSearchDropdown") {
               const validationResult = validate ? validate(inputValue) : "";
               setErrorMessage(validationResult || "");
               return !validationResult;
            } else {
               const validationResult = validate
                  ? validate(dropDownValuesHiddenRef.current?.value || "")
                  : "";
               setErrorMessage(validationResult || "");
               return !validationResult;
            }
         },
         getValue: () => {
            if (inputType !== "multipleSearchDropdown") {
               return inputValue;
            } else {
               return dropDownValuesHiddenRef.current?.value || "";
            }
         },
      }));

      const toggleDropDown = useCallback(() => {
         if (inputType === "multipleSearchDropdown") {
            if (validate) {
               setErrorMessage(validate(dropDownValuesHiddenRef.current?.value || ""));
            }
            setDropdownOpen((prevState) => !prevState);
         }
      }, [inputType, validate]);

      useEffect(() => {
         if (!dropdownOpen) return;

         function handleClickOutside(event) {
            if (
               componentRef.current &&
               !componentRef.current.contains(event.target)
            ) {
               toggleDropDown();
            }
         }

         document.addEventListener("mousedown", handleClickOutside);
         return () => {
            document.removeEventListener("mousedown", handleClickOutside);
         };
      }, [dropdownOpen, toggleDropDown]);

      const filteredOptions = options.filter((opt) =>
         opt.label.toLowerCase().includes(searchTerm.toLowerCase())
      );

      const handleSearchChange = (e) => {
         setSearchTerm(e.target.value);
      };

      const toggleOption = (option) => {
         const alreadySelected = selectedOptions.find(
            (o) => o.id === option.id
         );
         let newSelected;
         if (alreadySelected) {
            newSelected = selectedOptions.filter((o) => o.id !== option.id);
         } else {
            newSelected = [...selectedOptions, option];
         }
         setSelectedOptions(newSelected);

         const labels = newSelected.map((o) => o.label).join(", ");
         const ids = newSelected.map((o) => o.id).join(",");
         setInputValue(labels);
         if (dropDownValuesHiddenRef.current) {
            dropDownValuesHiddenRef.current.value = ids;
         }
      };

      const getInputElement = () => {
         if (inputType === "textarea") {
            return (
               <textarea
                  {...props}
                  value={inputValue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  className={`${errorMessage ? "input-error" : ""} ${className}`}
               />
            );
         } else if (inputType === "select") {
            return (
               <select
                  {...props}
                  value={inputValue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  className={`${errorMessage ? "input-error" : ""} ${className}`}
               >
                  {children}
               </select>
            );
         } else if (inputType === "multipleSearchDropdown") {
            return (
               <div className="multiple-search-dropdown-container" ref={componentRef}>
                  <input
                     {...props}
                     type="text"
                     placeholder={placeholder}
                     value={inputValue}
                     readOnly
                     onClick={toggleDropDown}
                     className={`multiple-search-dropdown-input ${
                        errorMessage ? "input-error" : ""
                     } ${className}`}
                  />
                  <input
                     type="hidden"
                     ref={dropDownValuesHiddenRef}
                     name={props.name || "selected_ids"}
                  />
                  {dropdownOpen && (
                     <div className="multiple-search-dropdown-list">
                        <div className="search-container">
                           <input
                              type="text"
                              value={searchTerm}
                              onChange={handleSearchChange}
                              placeholder="Zoeken..."
                           />
                        </div>
                        <div className="multiple-seach-options">
                           {filteredOptions.length > 0 ? (
                              filteredOptions.map((option) => {
                                 const checked = selectedOptions.some(
                                    (o) => o.id === option.id
                                 );
                                 return (
                                    <div key={option.id} className="dropdown-option">
                                       <CustomCheckbox
                                          checked={checked}
                                          text={option.label}
                                          onToggle={() => toggleOption(option)}
                                       />
                                    </div>
                                 );
                              })
                           ) : (
                              <div className="dropdown-no-results">
                                 Geen resultaten gevonden
                              </div>
                           )}
                        </div>
                     </div>
                  )}
               </div>
            );
         } else {
            return (
               <input
                  {...props}
                  type={inputType || "text"}
                  value={inputValue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  className={`${errorMessage ? "input-error" : ""} ${className}`}
               />
            );
         }
      };

      const labelElement = label && (
         <div className="info-container">
            <label className="input-label">{label}</label>
            {info && (
               <div 
                  style={{ position: "relative" }} 
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
               >
                  <img src={infoIcon} alt="Info" className="info-icon" />
                  {showTooltip && <div className="info-tooltip">{info}</div>}
               </div>
            )}
         </div>
      );

      return (
         <div className="input-container">
            {labelElement}
            {getInputElement()}
            {errorMessage && <span className="error-message">{errorMessage}</span>}
         </div>
      );
   }
);

export default InputWithChecks;
